import React from 'react';

import { Row, Col, Form, Button } from 'antd';

import { CloseCircleOutlined, PlusOutlined } from '@ant-design/icons';

import {
  GroupElementWrapper,
  FormItemText,
  FormItemNumber,
  FormItemBoolean,
  FormItemSelectSpecialEnum,
  FormItemSelectEnum,
  FormItemDatetime,
  FormItemAllergenSelectEnum,
  FormItemAllergenBoolean,
  FormItemContainer,
  renderInputFieldByVariant,
  FormItemTextArea,
} from 'pages/product-full-view/shared/components';

import {
  productDetailActionTypes,
  useProductDetail,
  useProductDetailDispatch,
} from '../ProductDetailContext';

import { findProductPropertyEnums, getFieldValueFromListIdx } from '../utils';
import { checkHiddenFormField, generateOriginalStringPath } from './utils';

import { NUMERIC_TYPE } from 'static/Constants';
import {
  SPECIAL_ENUMS,
  LIST_BOOLEAN_PROPERTY_NAME_NEED_TO_DISABLE,
  LIST_ALLERGEN_PROPERTY_NAME_CHECKABLE,
  LIST_PROPERTY_TYPE_TEXTAREA,
} from 'pages/product-full-view/constants';

export const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 4 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 24 },
  },
};

export const formItemLayoutWithOutLabel = {
  wrapperCol: {
    xs: { span: 24, offset: 0 },
    sm: { span: 24, offset: 0 },
  },
};

export const FormItemOverlayList = ({
  property,
  name,
  className,
  variant = 'text',
  originalProductModules,
  ...otherProps
}) => {
  const {
    overlay: { deletedFieldsOverlay },
  } = useProductDetail();

  const dispatch = useProductDetailDispatch();

  const handleDeleteFieldForm = ({ field, removeCallback }) => {
    const isNestedField = Array.isArray(name);
    const indexesField = isNestedField
      ? name.concat(field.fieldKey)
      : [name].concat(field.fieldKey);

    const originalValueFieldForm = getFieldValueFromListIdx(
      originalProductModules,
      indexesField
    );

    if (
      originalValueFieldForm === null ||
      originalValueFieldForm === undefined
    ) {
      removeCallback(field.name);
    } else {
      removeCallback(field.name);
      dispatch({
        type: productDetailActionTypes.UPDATE_DELETED_FIELDS_OVERLAY,
        pathField: generateOriginalStringPath(indexesField),
      });
    }
  };

  return (
    <Form.List name={name}>
      {(fields, { add, remove }) => {
        return (
          <Row className={className}>
            <FormItemContainer
              property={property}
              labelCol={{ span: 6 }}
              {...otherProps}
            >
              <Button
                type='default'
                onClick={() => add()}
                icon={<PlusOutlined />}
                style={{ borderRadius: 4 }}
              >
                Add value
              </Button>
            </FormItemContainer>

            <Col xs={6} />
            <Col flex={1}>
              <GroupElementWrapper
                style={{
                  display: fields.length === 0 ? 'none' : null,
                  marginRight: 40,
                  marginBottom: 12,
                  padding: '4px 12px 8px',
                  width: '96%',
                }}
              >
                {fields.map((field, index) => {
                  const fullPath = Array.isArray(name)
                    ? name.concat(field.fieldKey)
                    : [name].concat(field.fieldKey);

                  const stringPath = generateOriginalStringPath(fullPath);

                  if (deletedFieldsOverlay.includes(stringPath)) return null;

                  return (
                    <Row key={field.key}>
                      <Col flex='auto'>
                        <FormItemContainer
                          {...field}
                          {...(index === 0
                            ? formItemLayout
                            : formItemLayoutWithOutLabel)}
                        >
                          {renderInputFieldByVariant(
                            variant,
                            property.propertyDisplayName
                          )}
                        </FormItemContainer>
                      </Col>
                      {fields.length > 0 ? (
                        <Col flex='30px' style={{ textAlign: 'right' }}>
                          <Button
                            type='danger'
                            onClick={() => {
                              handleDeleteFieldForm({
                                field,
                                removeCallback: remove,
                              });
                            }}
                            icon={<CloseCircleOutlined />}
                            style={{
                              marginLeft: 4,
                              borderColor: '#ff4d4f',
                              backgroundColor: '#ff4d4f',
                              borderRadius: 4,
                            }}
                          />
                        </Col>
                      ) : null}
                    </Row>
                  );
                })}
              </GroupElementWrapper>
            </Col>
          </Row>
        );
      }}
    </Form.List>
  );
};

export const FormPrimitiveItemOverlay = ({
  property,
  nameFormItem,
  parentNestedIndex,
  productEnums,
  originalProductModules,
}) => {
  const { propertyType, isArray, listName, fieldFullPath } = property;

  const isEnumType = Boolean(listName);
  const isListType = Boolean(isArray);

  // Form Enum
  if (isEnumType) {
    const propertyEnums = findProductPropertyEnums(listName, productEnums);

    if (SPECIAL_ENUMS.includes(listName)) {
      return (
        <FormItemSelectSpecialEnum
          property={property}
          nameFormItem={nameFormItem}
          propertyEnums={propertyEnums}
          labelCol={{ span: 6 }}
        />
      );
    }

    if (LIST_ALLERGEN_PROPERTY_NAME_CHECKABLE.includes(fieldFullPath)) {
      return (
        <FormItemAllergenSelectEnum
          labelCol={{ span: 6 }}
          property={property}
          nameFormItem={nameFormItem}
          propertyEnums={propertyEnums}
          parentNestedIndex={parentNestedIndex}
        />
      );
    }

    return (
      <FormItemSelectEnum
        property={property}
        nameFormItem={nameFormItem}
        propertyEnums={propertyEnums}
        labelCol={{ span: 6 }}
      />
    );
  }

  // List
  if (isListType) {
    if (NUMERIC_TYPE.includes(property.propertyType)) {
      return (
        <FormItemOverlayList
          className='product-detail-view__overlay-form-list--non-nested'
          property={property}
          name={nameFormItem}
          variant='number'
          originalProductModules={originalProductModules}
        />
      );
    } else if (property.propertyType === 'datetime') {
      return (
        <FormItemOverlayList
          className='product-detail-view__overlay-form-list--non-nested'
          property={property}
          name={nameFormItem}
          variant='datetime'
          originalProductModules={originalProductModules}
        />
      );
    } else {
      return (
        <FormItemOverlayList
          className='product-detail-view__overlay-form-list--non-nested'
          property={property}
          name={nameFormItem}
          variant='text'
          originalProductModules={originalProductModules}
        />
      );
    }
  }

  // Basic form item
  if (propertyType === 'string') {
    const textareaProperty = LIST_PROPERTY_TYPE_TEXTAREA[fieldFullPath];
    if (textareaProperty) {
      return (
        <FormItemTextArea
          property={property}
          name={nameFormItem}
          style={{ marginBottom: '18px' }}
          {...textareaProperty}
        />
      );
    } else
      return (
        <FormItemText
          property={property}
          name={nameFormItem}
          labelCol={{ span: 6 }}
        />
      );
  }

  if (NUMERIC_TYPE.includes(propertyType)) {
    return (
      <FormItemNumber
        name={nameFormItem}
        property={property}
        labelCol={{ span: 6 }}
      />
    );
  }

  if (propertyType === 'datetime') {
    return (
      <FormItemDatetime
        name={nameFormItem}
        property={property}
        labelCol={{ span: 6 }}
      />
    );
  }

  if (propertyType === 'boolean') {
    const shouldCheckDisabled =
      LIST_BOOLEAN_PROPERTY_NAME_NEED_TO_DISABLE.includes(fieldFullPath);

    if (shouldCheckDisabled) {
      return (
        <FormItemAllergenBoolean
          parentNestedIndex={parentNestedIndex}
          name={nameFormItem}
          property={property}
          labelCol={{ span: 6 }}
        />
      );
    }

    return (
      <FormItemBoolean
        name={nameFormItem}
        property={property}
        labelCol={{ span: 6 }}
      />
    );
  }

  // Default form item
  return (
    <FormItemText
      property={property}
      name={nameFormItem}
      labelCol={{ span: 6 }}
    />
  );
};
